// client/src/components/SmartDataTable.js
import "../styles/Buttons.css";
import "./DataTable.css";
import React, { useState, useEffect, useMemo, useRef } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../api/api";

// toast confirm helper
function confirmToast(message, { confirmText = "Delete", cancelText = "Cancel" } = {}) {
  return new Promise((resolve) => {
    toast(({ closeToast }) => (
      <div style={{ display: "grid", gap: 8 }}>
        <div style={{ fontWeight: 600 }}>{message}</div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button
            onClick={() => { resolve(false); closeToast(); }}
            style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #ccc", background: "#fff" }}
          >{cancelText}</button>
          <button
            onClick={() => { resolve(true); closeToast(); }}
            style={{ padding: "6px 10px", borderRadius: 6, border: "none", background: "#e11d48", color: "#fff" }}
          >{confirmText}</button>
        </div>
      </div>
    ), { autoClose: false, closeOnClick: false, draggable: false });
  });
}

// replace :token and {token}
const buildUrlWithParams = (url, params = {}) => {
  if (!url) return url;
  const get = (k) => {
    const v = params[k];
    return v === undefined || v === null ? null : String(v);
  };
  let out = url.replace(/:([A-Za-z0-9_]+)/g, (m, key) => {
    const v = get(key);
    return v === null ? m : encodeURIComponent(v);
  });
  out = out.replace(/\{([A-Za-z0-9_]+)\}/g, (m, key) => {
    const v = get(key);
    return v === null ? m : encodeURIComponent(v);
  });
  return out;
};

const humanize = (k) =>
  k.replace(/([A-Z])/g, " $1").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Props:
 * - config?: {
 *     columns?: [{ value, label, width?, align?, type?('text'|'count'|'render'), render?(row), search?: string }],
 *     queryName?: string,
 *     pageSize?: number,
 *     startNum?: number,
 *     buttons?: [...],
 *     onAddNew?: string,
 *     fixedFilters?: object,
 *     pathParams?: object,
 *     deletePathTemplate?: string,
 *     deleteMethod?: 'DELETE'|'POST',
 *     // request buttons: { label, requestPath, requestMethod?, requestBody? }
 *     orgIdOverride?: string
 *   }
 * - columns?: same as config.columns (overrides)
 * - rows?: preload static rows (no fetch if no queryName)
 * - loading?: external loading flag (optional)
 */
const SmartDataTable = ({ config = {}, columns: columnsProp, rows: rowsProp, loading: loadingProp, resetOnMount = false }) => {
  const {
    columns: configColumns = [],
    queryName = "",
    pageSize: initialPageSize = 20,
    startNum = 1,
    buttons = [],
    onAddNew = null,
    fixedFilters,
    pathParams,
    orgIdOverride,
    deletePathTemplate,
    deleteMethod,
  } = config;

  // normalize columns to { value, label, ... }
  const normalizeCols = (cols = []) =>
    cols.map((c) => {
      const value = c.value ?? c.key;
      const label = c.label ?? c.title ?? (value ? humanize(String(value)) : "");
      const type  = c.type ?? (typeof c.render === "function" ? "render" : "text");
      return { ...c, value, label, type };
    });

  const definedColumns = useMemo(() => {
    if (Array.isArray(columnsProp) && columnsProp.length) return normalizeCols(columnsProp);
    if (Array.isArray(configColumns) && configColumns.length) return normalizeCols(configColumns);
    return [];
  }, [columnsProp, configColumns]);

  // only depend on the list of searchable keys (stable)
  const searchKeys = useMemo(() => {
    const src = definedColumns.length ? definedColumns : [];
    return src.map((c) => c.search).filter(Boolean);
  }, [definedColumns]);
  const searchKeysSig = useMemo(() => JSON.stringify(searchKeys), [searchKeys]);

  const [rows, setRows] = useState(Array.isArray(rowsProp) ? rowsProp : []);
  const [loading, setLoading] = useState(Boolean(loadingProp));
  const [filters, setFilters] = useState({});
  const [debouncedFilters, setDebouncedFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [totalPages, setTotalPages] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [busyAction, setBusyAction] = useState(null);
  const pageOffset = currentPage * pageSize;

  const abortRef = useRef(null);
  const busyActionRef = useRef(null);
  const navigate = useNavigate();

  // auto-generate columns if none provided (display only)
  const columns = useMemo(() => {
    if (definedColumns.length) return definedColumns;
    const sample = rowsProp?.[0] || rows?.[0];
    if (!sample) return [];
    const keys = Object.keys(sample).filter((k) => !k.startsWith("_") && k !== "__v");
    return keys.map((k) => ({ value: k, label: humanize(k), type: "text" }));
  }, [definedColumns, rowsProp, rows]);

  // init debouncedFilters to filters to avoid a duplicate first fetch
  useEffect(() => {
    setFilters({});
    setDebouncedFilters(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // debounce filters → debouncedFilters
  useEffect(() => {
    const t = setTimeout(() => setDebouncedFilters(filters), 300);
    return () => clearTimeout(t);
  }, [filters]);

  useEffect(() => {
    if (resetOnMount) {
      setFilters({});
      setDebouncedFilters({});
      setCurrentPage(0);
    }
  }, [resetOnMount]);

  useEffect(() => {
    handleResetFilters();
  }, []);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(0);
  };

  const handleResetFilters = () => {
    setFilters({});
    setDebouncedFilters({});
    setCurrentPage(0);
    setRefreshTrigger((p) => p + 1);
  };

  const handleSort = (key) => {
    if (key === sortBy) setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    else { setSortBy(key); setSortOrder("asc"); }
    setCurrentPage(0);
    setRefreshTrigger((p) => p + 1);
  };

  // sync external rows/loading when no query
  useEffect(() => { if (!queryName && Array.isArray(rowsProp)) setRows(rowsProp); }, [rowsProp, queryName]);
  useEffect(() => { if (!queryName && typeof loadingProp === "boolean") setLoading(loadingProp); }, [loadingProp, queryName]);

  // stable signatures for optional objects to avoid ref churn
  const fixedFiltersSig   = useMemo(() => JSON.stringify(fixedFilters || {}), [fixedFilters]);
  const pathParamsSig     = useMemo(() => JSON.stringify(pathParams   || {}), [pathParams]);
  const debouncedFiltersSig = useMemo(
    () => JSON.stringify(searchKeys.reduce((acc, k) => {
      if (debouncedFilters[k] !== undefined) acc[k] = debouncedFilters[k];
      return acc;
    }, {})),
    [debouncedFilters, searchKeysSig]
  );

  // StrictMode-safe fetch: NO signature guard; abort stale; run twice is ok
  useEffect(() => {
    if (!queryName) return;

    const searchParams = JSON.parse(debouncedFiltersSig || "{}");
    const payload = {
      ...searchParams,
      cnt: pageOffset,
      lmt: pageSize,
      ...(sortBy ? { sortBy, sortOrder } : {}), // ✅ guard empty sortBy
      sortOrder,
      ...(fixedFilters || {}),
    };
    const finalQueryName = buildUrlWithParams(queryName, pathParams || {});

    // abort previous in-flight
    try { abortRef.current?.abort(); } catch {}
    const controller = new AbortController();
    abortRef.current = controller;

    const run = async () => {
      try {
        setLoading(true);
        const data = await apiFetch(finalQueryName, {
          method: "POST",
          body: payload,
          orgIdOverride,
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;

        let items = [];
        let total = 0;
        if (Array.isArray(data)) {
          items = data;
          const countFromRow = parseInt(items[0]?.docCount || "0", 10);
          total = Number.isFinite(countFromRow) && countFromRow > 0 ? countFromRow : items.length;
        } else if (data && Array.isArray(data.items)) {
          items = data.items;
          total = parseInt(data.total ?? items.length, 10) || 0;
        } else if (data && Array.isArray(data.data)) { // fallback shape
          items = data.data;
          total = parseInt(data.total ?? items.length, 10) || items.length;
        }

        setRows(items);
        setTotalPages(total > 0 ? Math.ceil(total / pageSize) : 0);
      } catch (err) {
        if (controller.signal.aborted) return;
        console.error(err);
        toast.error("Error loading data.");
        setRows([]);
        setTotalPages(0);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    run();
    return () => { try { controller.abort(); } catch {} };
  }, [
    queryName,
    pageOffset,
    pageSize,
    sortBy,
    sortOrder,
    debouncedFiltersSig,
    fixedFiltersSig,
    pathParamsSig,
    orgIdOverride,
    refreshTrigger,
  ]);


  // deletion
  const confirmDelete = async (row) => {
    const base = deletePathTemplate || "/users/deletedoc/{_id}";
    console.log("base:", base)
    const path = buildUrlWithParams(base, row);
    console.log("path:", path)

    const method = (deleteMethod || "DELETE").toUpperCase();
    console.log("method:", method)

    await apiFetch(path, { method, orgIdOverride });
    setRows((prev) => prev.filter((r) => (r._id || r.id) !== (row._id || row.id)));
    setRefreshTrigger((p) => p + 1);
  };

  const displayNameForRow = (row) =>
    row.name || [row.firstName, row.lastName].filter(Boolean).join(" ") || row.title || row._id || "this item";

  const handleDelete = async (row) => {
    const ok = await confirmToast(`Are you sure you want to delete '${displayNameForRow(row)}'?`);
    if (!ok) return;
    try {
      await toast.promise(confirmDelete(row), {
        pending: "Deleting...",
        success: "Deleted.",
        error: { render({ data }) { return `Delete failed${data?.message ? `: ${data.message}` : ""}`; } },
      });
    } catch (e) {
      console.error(e);
    }
  };

  const handleRequestAction = async (button, row, buttonKey) => {
    if (busyActionRef.current) return;
    const path = buildUrlWithParams(button.requestPath, row);
    const body = typeof button.requestBody === "function"
      ? button.requestBody(row)
      : button.requestBody;
    const method = (button.requestMethod || "POST").toUpperCase();

    try {
      busyActionRef.current = buttonKey;
      setBusyAction(buttonKey);
      await toast.promise(
        apiFetch(path, {
          method,
          ...(body !== undefined ? { body } : {}),
          orgIdOverride,
        }),
        {
          pending: button.pendingMessage || "Copying...",
          success: button.successMessage || "Copied.",
          error: {
            render({ data }) {
              return `Copy failed${data?.message ? `: ${data.message}` : ""}`;
            },
          },
        }
      );
      setRefreshTrigger((previous) => previous + 1);
    } catch (error) {
      console.error(error);
    } finally {
      busyActionRef.current = null;
      setBusyAction(null);
    }
  };

  const renderCell = (row, col, rowKey, rowIndex, colIndex) => {
    const cellKey = `${rowKey}-${col.value}-${colIndex}`;
    const val = row?.[col.value];
    switch (col.type) {
      case "count":  return <td key={cellKey} style={{ textAlign: col.align }}>{startNum + rowIndex}</td>;
      case "render": {
        const content = typeof col.render === "function" ? col.render(row) : row[col.value];
        return <td key={cellKey}>{content ?? ""}</td>;
      }
      default:       return <td key={cellKey} style={{ textAlign: col.align }}>{val}</td>;
    }
  };

  // SmartDataTable.js — replace renderActions with this:
  const renderActions = (row) => {
    const resolvedButtons =
      typeof buttons === 'function'
        ? buttons(row)
        : buttons;

    if (!resolvedButtons || resolvedButtons.length === 0) {
      return <td />;
    }

    return (
      <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
        <div style={{ display: "inline-flex", gap: 8 }}>
          {resolvedButtons.map((btn, index) => {
            const buttonKey = `${row._id || row.id || "row"}-${index}`;
            if (btn.action === "delete" || btn.label === "Delete") {
              return (
                <button
                  style={{marginTop:"6px", fontSize: "13px"}}
                  key={index}
                  className={btn.class || "btn-delete"}
                  onClick={() => handleDelete(row)}
                >
                  {btn.label}
                </button>
              );
            }

            if (btn.requestPath) {
              return (
                <button
                  key={index}
                  className={btn.class || `btn-${btn.color || "gray"}`}
                  disabled={busyAction === buttonKey}
                  onClick={() => handleRequestAction(btn, row, buttonKey)}
                >
                  {busyAction === buttonKey ? (btn.busyLabel || "Copying...") : btn.label}
                </button>
              );
            }

            return (
              <button
                key={index}
                  className={btn.class || `btn-${btn.color || "gray"}`}
                  onClick={() =>
                    navigate(
                      buildUrlWithParams(
                        btn.action,
                        row
                      )
                    )
                  }
              >
                {btn.label}
              </button>
            );
          })}
        </div>
      </td>
    );
  };


  const hasSearchableColumns = useMemo(() => (columns || []).some((c) => !!c.search), [columns]);
  const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;

  return (
    <div className="datatable-container compact">
    <div
      className="datatable-header-row"
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {onAddNew && (
          <button className="btn-green add-new-button" onClick={() => navigate(onAddNew)}>
            Add New
          </button>
        )}
        {/* Extra actions on the left (e.g., Add New Template) */}
        {config.headerActions &&
          (typeof config.headerActions === 'function' ? config.headerActions() : config.headerActions)}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <label htmlFor="pageSizeSelect">Rows per page:</label>
        <select
          id="pageSizeSelect"
          value={pageSize}
          onChange={(e) => {
            const ps = parseInt(e.target.value, 10);
            setPageSize(ps);
            setCurrentPage(0);
            setRefreshTrigger((prev) => prev + 1);
            config.pageSize = ps;
          }}
        >
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
      </div>
    </div>

      <div className="datatable-scroll-wrapper">
        <table className="datatable">
          <thead className="sticky-header">
            <tr>
              {columns.map((col, i) => (
                <th
                  key={i}
                  style={{ width: col.width || "auto", textAlign: col.align, cursor: "pointer" }}
                  onClick={() => handleSort(col.value)}
                >
                  {col.label} {sortBy === col.value ? (sortOrder === "asc" ? "▲" : "▼") : ""}
                </th>
              ))}
              {buttons.length > 0 && <th>Actions</th>}
            </tr>
            {hasSearchableColumns && !isMobile && (
              <tr className="search-row">
                {columns.map((col, i) => (
                  <th key={i}>
                    {i === 0 ? (
                      <button className="btn-cancel" onClick={handleResetFilters}>Reset</button>
                    ) : (
                      col.search && (
                        <div className="search-wrapper">
                          <input
                            type="text"
                            value={filters[col.search] || ""}
                            placeholder={``}
                            onChange={(e) => handleFilterChange(col.search, e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
                          />
                          {filters[col.search] && (
                            <span className="clear-x" onClick={() => handleFilterChange(col.search, "")}>×</span>
                          )}
                        </div>
                      )
                    )}
                  </th>
                ))}
                {buttons.length > 0 && <th></th>}
              </tr>
            )}
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={(columns?.length || 0) + (buttons.length > 0 ? 1 : 0)}>Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={(columns?.length || 0) + (buttons.length > 0 ? 1 : 0)}>No records found.</td></tr>
            ) : (
              rows.map((row, rowIndex) => {
                const rowKey = row._id || row.id || String(rowIndex);
                return (
                  <tr key={rowKey}>
                    {columns.map((col, colIndex) => renderCell(row, col, rowKey, rowIndex, colIndex))}
                    {buttons.length > 0 && renderActions(row)}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {!config.hideFooter && (
      <div className="datatable-footer">
        <div className="datatable-controls">
          <button onClick={() => setCurrentPage(0)} disabled={currentPage === 0}>First</button>
          <button onClick={() => setCurrentPage((p) => Math.max(p - 1, 0))} disabled={currentPage === 0}>Previous</button>
          <span>Page {totalPages === 0 ? 0 : currentPage + 1} of {totalPages}</span>
          <button onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages - 1))} disabled={currentPage + 1 >= totalPages}>Next</button>
          <button onClick={() => setCurrentPage(totalPages - 1)} disabled={currentPage + 1 >= totalPages}>Last</button>
        </div>
      </div>)}
    </div>
  );
};

export default SmartDataTable;
